import {IActionable} from "/@back@/commons/actionables.js";
import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {GridColDef, ICellBuilder, IRowSortFn} from "/@back@/commons/widgets/grid-core.js";
import {GridDataHolderJsonArray, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {GridSmall} from "/@back@/commons/widgets/grid-tags.js";
import {AppFrameDeskFeat} from "/@back@/core/appFrame.js";
import {JWspAppDef, JWspDocAppDef} from "/@back@/wsp/plugins/wspsPlg.js";
import {Action, IAction} from "/@lib@/commons/actions.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {renderAppend, TemplateResult, xhtml} from "/@lib@/commons/utils/htmlLit.js";
import {IView} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {ChainUniverse, IChainEnv} from "/@lib@/wsp/chain.js";
import {IWspUiEnv, JInfoWsp, JWspInfoInList, Wsp, WSP} from "/@lib@/wsp/wsp.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {LANG} from "/@lib@/commons/lang.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {OpenPropsWspAction} from "/@back@/wsp/actions/wspActions.js";
import "/@back@/wsp/views/views_Perms.js";
import {WSPPACK} from "/@lib@/wsp/pack.js";


/** View de sélection et d'actions sur un atelier. */
export interface WspList extends BaseElement, IView {
	initialize(init: OSkinableInit): this;
}

export class WspList extends BaseElement implements IActionable<Element>, ICellBuilder {

	reg: IReg<IChainEnv>;

	universe: ChainUniverse;

	/* Simulation d'un IActionable, pour navigation dans le menu. */
	action: Action<Element> = new Action(); //fake pour
	actionContext: Element;

	get disabled() { return true }

	cellTpl: (this: void, row: GridDataRowJson<JWspInfoInList>, wspList: WspList, reg: IReg<IChainEnv>) => TemplateResult;

	_filterInput: HTMLInputElement;
	_grid: GridSmall;
	_datas: GridDataHolderJsonArray<JWspInfoInList>;
	_filterdDatas: GridDataHolderJsonArray<JWspInfoInList>;
	_fetchState: 'fetching' | 'fetchFailed' | 'fetchDone';
	_actionsInCell: IAction<IRegPointer<IWspUiEnv>>[];
	_defaultAction: OpenWsp;

	protected _initialize(init: OSkinableInit) {
		this.reg = this.findReg(init);
		this.universe = this.reg.env.universe;
		this._datas = new GridDataHolderJsonArray();

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.tabIndex = 0;
		this._filterInput = sr.appendChild(<div id="search" hidden=""/>).appendChild(<input type="search" spellcheck="false" placeholder="Filtrer..." aria-label="Filtrer" oninput={this.onFilterInput} onkeydown={this.onFilterKeydown}/>) as HTMLInputElement;
		this._grid = sr.appendChild(new GridSmall().initialize({
			hideHeaders: true,
			selType: 'monoOver',
			columnDefs: [new GridColDef('wsps').setFlex('90vw', 0, 1).setCellBuilder(this)],
			dataHolder: this._datas,
			emptyBody: () => {
				if (this._fetchState === 'fetchFailed') return <c-msg level="error" label="Chargement en échec"/>;
				if (this._fetchState === 'fetchDone') {
					if (this._filterInput.value) return <c-msg level="info" label="Aucun atelier pour ce filtre"/>;
					return <c-msg level="info" label="Aucun atelier disponible"/>;
				}
				return <c-msg level="info" label="Chargement en cours..."/>;
			},
			skinOver: 'wsp-list/c-grid',
			skinScroll: 'scroll/small'
		}));
		this._grid.setAttribute('id', "grid");
		this._datas.connectToGrid(this._grid);

		// incompatible avec sous-menu
		// this._grid.uiEvents.on('rowDblclick', (row: GridDataRowJson<JWspInfoInList> | null, ev: MouseEvent) => {
		// 	if (!row) return;
		// 	let wspCd = row.rowDatas.wspCd;
		// 	(desk as AppFrameDeskFeat).findAndOpenApp({wsp: wspCd} as JWspAppDef);
		// 	this.dispatchEvent(new CustomEvent('c-actioned', {detail: {actionable: this}, bubbles: true, composed: true}));
		// });

		this._grid.uiEvents.on('rowClick', (row: GridDataRowJson<JWspInfoInList> | null, ev: MouseEvent) => {
			if (row) this.gotoWsp(row.rowDatas, ev);
		});

		// this._grid.addEventListener('keydown', function (this: GridSmall, ev: KeyboardEvent) {
		// 	if (ev.key === 'ArrowRight') onKey.call(this)
		// });

		this._grid.addEventListener('keydown', function (this: GridSmall, ev: KeyboardEvent) {
			if (ev.key === ' ' || ev.key === 'Enter') {
				const row = this.dataHolder.getRow(this.getSelectedRow()) as GridDataRowJson<JWspInfoInList>;
				if (row) DOMSH.findHost<WspList>(this).gotoWsp(row.rowDatas, ev);
			} else if (ev.key.length === 1) {
				DOMSH.findHost<WspList>(this)._filterInput.focus();
			}
		});

		const defaultMode = WSP.defaultWspOpenMode(this.reg);
		this._defaultAction = defaultMode ? new OpenWsp(defaultMode) : null;
		const altMode = WSP.alternateWspOpenMode(this.reg);
		this._actionsInCell = altMode && altMode !== defaultMode ? [new OpenWsp(altMode).setLabel(null)] : null;

		this.cellTpl = this.reg.getSvc("wspListCellTpl") || ((row: GridDataRowJson<JWspInfoInList>, wspList: WspList, reg: IReg<IChainEnv>): TemplateResult => {
			const wspInfo = row.rowDatas;
			//console.log("::::::", wspDef.props);
			const wspReg: IReg<IWspUiEnv> = REG.createSubReg(reg, new Wsp(reg.env.universe.wspServer, wspInfo.wspCd, wspInfo as JInfoWsp)) as IReg<IWspUiEnv>;
			const wspCanOpen = wspReg.hasPerm("action.wspList#open.wsp") && (wspReg.hasPerm("ui.wspApp") || wspReg.hasPerm("ui.wspDocApp"));
			let wspInfoTxt: string;
			if (!wspCanOpen) {
				wspInfoTxt = "Accès non autorisé";
			} else {
				switch (wspInfo.status) {
				case 'ok' :
					wspInfoTxt = wspInfo.wspTypeWarn === 'unknown' ? "Modèle inconnu" : WSPPACK.buildWspDefTitle(wspInfo.wspType, {lang: false, version: false});
					break;
				case 'failed' :
					wspInfoTxt = 'Modèle en erreur';
					break;
				default:
					wspInfoTxt = wspInfo.wspType ? WSPPACK.buildWspDefTitle(wspInfo.wspType, {lang: false, version: false}) : '...'; //non chargé ou en cours
				}
			}
			const actionsToolbar = new BarActions<IRegPointer<IWspUiEnv>>().initialize({
				actions: this._actionsInCell,
				uiContext: "bar",
				actionContext: {
					reg: wspReg,
				}
			});
			actionsToolbar.addEventListener("click", (ev) => {
				ev.preventDefault();
				ev.stopImmediatePropagation();
			});
			return xhtml`
<span id="icon" aria-disabled="${!wspCanOpen}" class="${(wspInfo.props.drfRefWsp ? 'drf' : wspInfo.props.drvMasterWsp ? 'drv' : 'wsp') + (wspInfo.props.publicWsp === 'true' ? ' public' : '')}"/>
<div id="body" aria-disabled="${!wspCanOpen}">
	<span id="title">${wspInfo.title || wspInfo.wspCd}</span>
	<div id="wspType">${wspInfoTxt}</div>
</div>
${actionsToolbar}`;
		});
		this.fetchDatas();
	}

	async fetchDatas() {
		this._fetchState = "fetching";
		try {
			const datas = await WSP.listWsps(this.universe.wspServer, {fields: ["srcRoles", "srcRi"]});
			datas.wsps.sort((w1, w2) => (w1.title || w1.wspCd).localeCompare(w2.title || w2.wspCd));
			DOM.setHidden(this._filterInput.parentElement, datas.wsps.length < 8);
			this._fetchState = "fetchDone"; //avant le setDatas pour l'eval du ifEmpty.
			this._datas.setDatas(datas.wsps);
			this._grid.setSelectedRows(0);
			this._grid.focus();
		} catch (e) {
			this._fetchState = "fetchFailed";
			this._datas.setDatas([]);
			throw e;
		}

	}

	gotoWsp(wspInfo: JWspInfoInList | null, ev: KeyboardEvent | MouseEvent) {
		if (!wspInfo || !this._defaultAction) return;
		this._defaultAction.executeIfAvailable({reg: REG.createSubReg(this.reg, new Wsp(this.reg.env.universe.wspServer, wspInfo.wspCd, wspInfo as JInfoWsp))}, ev);
		this.dispatchEvent(new CustomEvent('c-actioned', {detail: {actionable: {action: this._defaultAction}}, bubbles: true, composed: true}))
		//this.getPopup(row.rowDatas).show({initX: ev.pageX - 10, initY: ev.pageY - 10}, this, this);
	}

	focus() {
		this._grid.focus();
	}

	/** api ICellBuilder pour dessiner chaque wsp. */
	redrawCell(row: GridDataRowJson<JWspInfoInList>, root: HTMLElement) {
		const sh = root.shadowRoot || this._buildCell(root);
		renderAppend(this.cellTpl(row, this, this.reg), sh);
	}

	protected _buildCell(root: HTMLElement): ShadowRoot {
		if (this._defaultAction) root.title = this._defaultAction.getDescription(this);
		const sh = root.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin('wsp-list/cellInList', sh);
		return sh;
	}

	protected onFilterInput(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<WspList>(this);
		const text = this.value;
		if (text) {
			const pattern = new RegExp(LANG.escape4RegexpFuzzy(text), 'i');
			if (!me._filterdDatas) me._filterdDatas = new GridDataHolderJsonArray();
			const fullArr = me._datas.getDatas();
			const arr = [] as JWspInfoInList[];
			for (let i = 0, s = fullArr.length; i < s; i++) {
				const v = fullArr[i];
				if (pattern.test(v.title || v.wspCd)) arr.push(v);
			}
			me._filterdDatas.setDatas(arr);
			if (me._grid.dataHolder !== me._filterdDatas) me._grid.dataHolder = me._filterdDatas;
		} else {
			me._grid.dataHolder = me._datas;
		}
	}

	protected onFilterKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === "ArrowDown") {
			const me = DOMSH.findHost<WspList>(this);
			if ((me._filterdDatas || me._datas).countRows() > 0) {
				me._grid.setSelectedRows(0);
				me._grid.focus();
				ev.preventDefault();
				ev.stopImmediatePropagation();
			}
		}
	}

	/** api ICellBuilder, pas de tri */
	getColSortFn(): IRowSortFn {return null};
}

/**
 * ATTENTION wspMetaUi n'est pas chargé dans le Wsp du contexte de cette action.
 */
export class OpenWsp extends Action<IRegPointer<IWspUiEnv>> {
	constructor(public wspAppMode?: 'wspApp' | 'wspDocApp', id?: string) {
		super(id || "wspOpen");
		this._label = "Ouvrir...";
		this._description = wspAppMode === 'wspApp' ? "Ouvrir l'atelier en mode explorateur" : "Ouvrir l'atelier en mode plan";
		this.setIcon(wspAppMode === 'wspApp' ? "/@skin@/wsp/views/spaceTree/tree.svg" : "/@skin@/wsp/views/toc/toc.svg");
		this.requireEnabledPerm("action.wspList#open.wsp");
		this.requireVisiblePerm(wspAppMode === 'wspApp' ? "ui.wspApp" : "ui.wspDocApp");
	}

	isVisible(ctx: IRegPointer<IWspUiEnv>): boolean {
		if (!ctx.reg.env.wsp) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IRegPointer<IWspUiEnv>, ev?: KeyboardEvent | MouseEvent) {
		const infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		const {OpenPropsWspAction} = await import("/@back@/wsp/actions/wspActions.js");
		if (ctx.reg.env.wsp.isLoaded && ctx.reg.env.wsp.isInError) {
			POPUP.showNotifError("Cet atelier est en erreur et ne peut pas être ouvert. Veuillez vérifier ses propriétés.", document.body);
			new OpenPropsWspAction().executeIfAvailable(ctx);
		} else if (infoWsp && infoWsp.wspTypeWarn === 'unknown') {
			POPUP.showNotifForbidden("Le modèle documentaire de cet atelier est inconnu : son ouverture est impossible. Veuillez vérifier les propriétés de l'atelier.", document.body);
			new OpenPropsWspAction().executeIfAvailable(ctx);
		} else {
			(desk as AppFrameDeskFeat).findAndOpenApp(
				this.wspAppMode === "wspApp" ?
					{u: ctx.reg.env.universe.getId(), wsp: ctx.reg.env.wsp.code} as JWspAppDef
					: {u: ctx.reg.env.universe.getId(), wspDoc: ctx.reg.env.wsp.code} as JWspDocAppDef,
				ev);
		}
	}
}

REG.reg.registerSkin('wsp-list', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#search {
		padding: .3em;
		border-bottom: 1px solid var(--border-color);
	}

	input {
		background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		background-position: left;
		color: var(--form-color);
		border: none;
		padding-block: .2em;
		padding-inline: 1.2em .2em;
		width: 100%;
		font-size: inherit;
	}

	input:focus {
		outline: none;
	}

	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}

	input:focus::placeholder {
		color: transparent;
	}

	#grid {
		border: none;
	}

	#grid:focus {
		outline: none;
	}
`);

REG.reg.registerSkin('wsp-list/c-grid', 1, /* language=CSS */ `
	.inSel {
		background-color: var(--row-bgcolor);
	}

	c-msg {
		margin: .2em .5em;
	}
`);

REG.reg.registerSkin('wsp-list/cellInList', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	*[aria-disabled=true] {
		opacity: 70%;
	}

	#icon {
		height: 1.2em;
		width: 1.2em;
		min-width: 1.2em;
		filter: var(--filter);
	}

	.wsp {
		background: url('/@skin@/wsp/objects/wsp/wsp.svg') no-repeat center/contain;
	}

	.wsp.public {
		background: url('/@skin@/wsp/objects/lockOpen.svg') no-repeat top right/.7em, url('/@skin@/wsp/objects/wsp/wsp.svg') no-repeat center/contain;
	}

	.drf {
		background: url('/@skin@/wsp/objects/wsp/wspDrf.svg') no-repeat center/contain;
	}

	.drf.public {
		background: url('/@skin@/wsp/objects/lockOpen.svg') no-repeat top right/.7em, url('/@skin@/wsp/objects/wsp/wspDrf.svg') no-repeat center/contain;
	}

	.drv {
		background: url('/@skin@/wsp/objects/wsp/wspDrv.svg') no-repeat center/contain;
	}

	.drv.public {
		background: url('/@skin@/wsp/objects/lockOpen.svg') no-repeat top right/.7em, url('/@skin@/wsp/objects/wsp/wspDrv.svg') no-repeat center/contain;
	}

	#body {
		flex: 1;
		display: flex;
		flex-direction: column;
		margin-inline-start: .3em;
		margin-block: .2em;
		max-width: 40vw;
	}

	#title {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	#wspType {
		font-size: .7em;
		color: var(--fade-color);
	}

	c-action:not(:hover) {
		background-color: var(--row-bgcolor);
	}
`);

customElements.define('wsp-list', WspList);
